<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { happyApi, type HappyRow } from '$lib/api/happyCode';
  import { customerCareApi } from '$lib/api/customerCare';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { peopleApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import { canUseHappyQueue, type QueueReason } from '$lib/utils/happyCode';
  import { buildCallQueue, callKinds, callKindOrder, isLate, telHref, whatsappHref, type CallEntry, type CallKind } from '$lib/utils/callQueue';
  import { istDay, relativeDay, shortDay, plural } from '$lib/utils/touchpoints';
  import { initials } from '$lib/utils/salesHome';
  import { beginContactCall, consumeContactCall } from '$lib/utils/pendingCall';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { roleProfile, workflowAccessKey } from '$lib/utils/roleProfile';
  import type { Activity, Person } from '$lib/types';
  import type { CareIssue } from '$lib/api/customerCare';
  import Modal from '../ui/Modal.svelte';
  import HappyCallModal from './HappyCallModal.svelte';
  import CustomerCallForm from './CustomerCallForm.svelte';
  import Composer from './Composer.svelte';

  // The coordinator's daily list: everyone to ring today, from every source,
  // in priority order. Outcomes go back through each source's own workflow.
  const canAccess = $derived(canUseHappyQueue(session.me?.user, session.me?.happy_code_queue_access ?? false, session.me?.verticals));
  const scopeKey = $derived([roleProfile(session.me), session.me?.user.id, workflowAccessKey(session.me)]);
  const query = createQuery(() => ({ queryKey: ['happy-code-queue', ...scopeKey], queryFn: happyApi.queue, enabled: canAccess }));
  const settings = createQuery(() => ({ queryKey: ['completion-queue-settings', ...scopeKey], queryFn: happyApi.queueSettings, enabled: canAccess }));
  const fixes = createQuery(() => ({ queryKey: ['call-queue-fixes', 'sales-only', ...scopeKey], queryFn: () => happyApi.queueFixes(), enabled: canAccess }));
  const overdue = createQuery(() => ({ queryKey: ['call-queue-mine', 'overdue', ...scopeKey], queryFn: () => touchpointsApi.list({ scope: 'mine', bucket: 'overdue', per_page: 50 }), enabled: canAccess }));
  const today = createQuery(() => ({ queryKey: ['call-queue-mine', 'today', ...scopeKey], queryFn: () => touchpointsApi.list({ scope: 'mine', bucket: 'today', per_page: 50 }), enabled: canAccess }));
  let quietDays = $state(7);
  try { quietDays = Number(localStorage.getItem('call-queue:quiet-days')) || 7; } catch { /* Default. */ }
  const quiet = createQuery(() => ({ queryKey: ['call-queue-quiet', quietDays, ...scopeKey], queryFn: () => customerCareApi.list('reconnect', 1, null, 3, quietDays, { perPage: 30, businessOnly: true }), enabled: canAccess }));
  const todayDay = istDay();

  const entries = $derived(buildCallQueue({
    happy: query.data,
    fixes: fixes.data?.need_to_speak?.results ?? [],
    mine: [...(overdue.data?.results ?? []), ...(today.data?.results ?? [])],
    quiet: quiet.data?.reconnect?.results ?? []
  }));
  const sources = [query, fixes, overdue, today, quiet];
  const loading = $derived(sources.some(q => q.isPending));
  const failures = $derived(sources.filter(q => q.isError).map(q => getErrorMessage(q.error)));
  const counts = $derived(Object.fromEntries(callKindOrder.map(kind => [kind, entries.filter(e => e.kind === kind).length])) as Record<CallKind, number>);
  const lateCount = $derived(entries.filter(e => isLate(e)).length);
  const dueCount = $derived(entries.filter(e => e.kind !== 'reconnect').length);

  let filter = $state<'all' | CallKind>('all'), search = $state(''), focusKey = $state<string | null>(null), notice = $state('');
  const shown = $derived(entries.filter(e => (filter === 'all' || e.kind === filter) && (!search.trim() || `${e.name} ${e.place} ${e.reason} ${e.phone}`.toLowerCase().includes(search.trim().toLowerCase()))));
  const current = $derived(shown.find(e => e.key === focusKey) ?? shown[0] ?? null);
  const position = $derived(current ? shown.indexOf(current) + 1 : 0);

  // Logging windows, one per source workflow.
  let happyLog = $state<{ row: HappyRow; reasons: QueueReason[] } | null>(null);
  let fixLog = $state<CareIssue | null>(null);
  let composer = $state<{ personId?: number; activity?: Activity | null } | null>(null);
  let dialing = $state<string | null>(null), dialError = $state('');

  function log(entry: CallEntry) {
    notice = '';
    if (entry.happy) happyLog = entry.happy;
    else if (entry.issue) fixLog = entry.issue;
    else if (entry.activity?.status === 'scheduled') composer = { activity: entry.activity };
    else if (entry.personId) composer = { personId: entry.personId };
  }
  function skip() {
    if (!current) return;
    const next = shown[shown.indexOf(current) + 1] ?? shown[0];
    focusKey = next?.key ?? null;
  }
  function focus(entry: CallEntry) {
    focusKey = entry.key;
    document.querySelector('.tym-page')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  /** Dial: contacts are looked up for their current number, then the log opens on return. */
  async function dial(entry: CallEntry, whatsapp = false) {
    dialError = '';
    if (entry.phone && (entry.happy || entry.issue || entry.contact)) {
      // Contacts reopen the log when you come back from the call.
      if (entry.contact) beginContactCall({ id: entry.contact.id } as Person, whatsapp ? 'whatsapp' : 'call');
      window.open(whatsapp ? whatsappHref(entry.phone) : telHref(entry.phone), whatsapp ? '_blank' : '_self');
      return;
    }
    if (!entry.personId) { dialError = 'No phone number recorded.'; return; }
    const popup = whatsapp ? window.open('about:blank', '_blank') : null;
    dialing = entry.key;
    try {
      const person = await peopleApi.get(entry.personId);
      const number = whatsapp ? person.whatsapp || person.phone : person.phone;
      if (!number) throw new Error(`No phone number on ${person.first_name}'s contact. Open the contact to add one.`);
      if (!entry.happy && !entry.issue) beginContactCall(person, whatsapp ? 'whatsapp' : 'call');
      if (whatsapp && popup) { popup.opener = null; popup.location.href = whatsappHref(number); }
      else window.location.href = telHref(number);
    } catch (err) {
      popup?.close();
      dialError = getErrorMessage(err);
    } finally {
      dialing = null;
    }
  }
  function returned() {
    const call = consumeContactCall();
    if (!call) return;
    const entry = entries.find(e => e.personId === call.personId && !e.happy && !e.issue);
    if (entry) log(entry);
    else composer = { personId: call.personId };
  }
  function saved(message: string) {
    notice = message;
    happyLog = null; fixLog = null; composer = null;
    focusKey = null;
    for (const q of sources) void q.refetch();
  }
  function setQuiet(days: number) {
    quietDays = days;
    try { localStorage.setItem('call-queue:quiet-days', String(days)); } catch { /* Per-device only. */ }
  }
  function when(entry: CallEntry): { text: string; tone: string } {
    if (isLate(entry)) return { text: `Late · ${relativeDay(entry.deadline).toLowerCase()}`, tone: 'day-chip-danger' };
    if (entry.kind === 'unhappy') return { text: entry.day && entry.day < todayDay ? 'Call today · last day' : 'Call today', tone: 'day-chip-danger' };
    if (entry.kind === 'reconnect') return { text: entry.contact?.last_spoken_at ? `Quiet ${entry.contact.days_since_spoken} days` : 'Never spoken', tone: 'day-chip-info' };
    if (entry.time) return { text: entry.day && entry.day < todayDay ? `${shortDay(entry.day)}, ${entry.time}` : `Today ${entry.time}`, tone: 'day-chip-warn' };
    if (!entry.day || entry.day <= todayDay) return { text: 'Due today', tone: 'day-chip-ok' };
    return { text: shortDay(entry.day), tone: 'day-chip-info' };
  }
  const tones: Record<string, string> = { danger: '#c8412b', info: '#226a73', warn: '#d08700', ok: '#7ab000', muted: '#8a9196' };
</script>

<svelte:window onfocus={returned} />
{#if canAccess}
<div class="tym-page queue">
  <section class="top">
    <div class="day-card-dark hero" style={current ? `--accent:${tones[callKinds[current.kind].tone]}` : ''}>
      {#if loading && !entries.length}
        <p class="hero-eyebrow">Loading your calls…</p>
      {:else if current}
        {@const w = when(current)}
        <div class="hero-head">
          <p class="hero-eyebrow"><span class="dot"></span>{callKinds[current.kind].label} · {position} of {shown.length}</p>
          <span class="day-chip {w.tone}">{w.text}</span>
        </div>
        <h2 class="hero-name">{current.name}</h2>
        <p class="hero-place">{[current.place, current.reason].filter(Boolean).join(' · ')}</p>
        {#if current.scores.length}
          <div class="hero-scores">{#each current.scores as s}<span class:low={s.low}><strong>{s.score}/5</strong> {s.label}</span>{/each}</div>
        {/if}
        {#if current.quote}<blockquote>“{current.quote}”</blockquote>{/if}
        <p class="ask"><strong>Ask</strong> {callKinds[current.kind].prompt}</p>
        <div class="hero-actions">
          <button type="button" class="call" disabled={dialing === current.key} onclick={() => dial(current)}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"></path></svg>
            {dialing === current.key ? 'Finding number…' : current.phone ? `Call ${current.phone}` : 'Call'}
          </button>
          <button type="button" class="ghost" onclick={() => dial(current, true)}>WhatsApp</button>
          <button type="button" class="light" onclick={() => log(current)}>Log the call</button>
          {#if shown.length > 1}<button type="button" class="ghost" onclick={skip}>Skip →</button>{/if}
        </div>
        {#if dialError}<p class="hero-error" role="alert">{dialError}</p>{/if}
      {:else}
        <div class="cleared">
          <p class="hero-eyebrow">All done</p>
          <h2 class="hero-name">Call queue cleared.</h2>
          <p class="hero-place">{filter === 'all' && !search ? 'Nobody is waiting for a call. New feedback and call backs will appear here automatically.' : 'Nothing matches this filter.'}</p>
        </div>
      {/if}
    </div>

    <div class="stats">
      <div class="day-card summary">
        <div>
          <span class="big">{dueCount}</span>
          <p class="day-meta">{plural(dueCount, 'call')} to make today{#if counts.reconnect}{' · '}{counts.reconnect} to reconnect{/if}</p>
        </div>
        <div class="done">
          <span class="big small" class:late={lateCount > 0}>{lateCount}</span>
          <p class="day-meta">{lateCount ? 'late — call these first' : 'late — all on time'}</p>
        </div>
      </div>
      <div class="tiles">
        {#each callKindOrder as kind (kind)}
          <button type="button" class="tile" aria-pressed={filter === kind} style={`--accent:${tones[callKinds[kind].tone]}`} onclick={() => { filter = filter === kind ? 'all' : kind; focusKey = null; }}>
            <span class="tile-n">{counts[kind]}</span>
            <span class="tile-l">{callKinds[kind].plural}</span>
          </button>
        {/each}
      </div>
    </div>
  </section>

  {#if notice}<p class="saved" role="status">✓ {notice}</p>{/if}
  {#each failures as failure}<p class="tym-error-state" role="alert">Some calls could not be loaded: {failure} <button type="button" class="day-link" onclick={() => sources.forEach(q => q.refetch())}>Retry</button></p>{/each}

  <section class="day-card list">
    <div class="list-head">
      <div class="day-segment scroll" role="group" aria-label="Call type">
        <button type="button" aria-pressed={filter === 'all'} onclick={() => { filter = 'all'; focusKey = null; }}>All · {entries.length}</button>
        {#each callKindOrder as kind (kind)}
          {#if counts[kind]}<button type="button" aria-pressed={filter === kind} onclick={() => { filter = kind; focusKey = null; }}>{callKinds[kind].plural} · {counts[kind]}</button>{/if}
        {/each}
      </div>
      <input class="input search" type="search" aria-label="Search calls" placeholder="Search name, site or number…" bind:value={search} />
    </div>
    {#if filter === 'all' || filter === 'reconnect'}
      <p class="day-meta rule">
        Reconnect lists business contacts nobody has spoken to in
        <select class="quiet" aria-label="Quiet for" value={quietDays} onchange={e => setQuiet(Number(e.currentTarget.value))}>
          {#each [3, 5, 7, 14, 30] as d}<option value={d}>{d} days</option>{/each}
        </select>.
      </p>
    {/if}

    {#each shown as entry (entry.key)}
      {@const w = when(entry)}
      <article class="day-row item" class:active={current?.key === entry.key} style={`--accent:${tones[callKinds[entry.kind].tone]}`}>
        <span class="stripe" aria-hidden="true"></span>
        <span class="avatar" aria-hidden="true">{initials(entry.name.split(' ')[0], entry.name.split(' ').slice(1).join(' '))}</span>
        <button type="button" class="main" onclick={() => focus(entry)}>
          <span class="line1"><strong>{entry.name}</strong><span class="kind">{callKinds[entry.kind].label}</span></span>
          <span class="line2">{[entry.reason, entry.place].filter(Boolean).join(' · ')}</span>
          {#if entry.quote}<span class="line3">“{entry.quote}”</span>{/if}
        </button>
        {#if entry.scores.some(s => s.low)}<span class="day-chip day-chip-danger">Lowest {Math.min(...entry.scores.map(s => s.score))}/5</span>{/if}
        <span class="day-chip {w.tone} when">{w.text}</span>
        <div class="actions">
          <button type="button" class="day-mini-btn call-mini" disabled={dialing === entry.key} onclick={() => dial(entry)}>Call</button>
          <button type="button" class="day-mini-btn" onclick={() => log(entry)}>Log</button>
          {#if entry.personId}<a class="day-mini-btn" href={`#/people/${entry.personId}`} onclick={(e) => { e.preventDefault(); route.push({ tab: 'people', id: entry.personId! }); }}>Contact</a>{/if}
        </div>
      </article>
    {:else}
      {#if !loading}<p class="tym-empty-state">{search || filter !== 'all' ? 'Nothing matches.' : 'Nobody to call right now.'}</p>{/if}
    {/each}
  </section>

  <details class="rules">
    <summary>How the call queue works</summary>
    <ul>
      {#each callKindOrder as kind}<li><strong>{callKinds[kind].label}</strong> — {callKinds[kind].rule}</li>{/each}
    </ul>
  </details>
</div>

{#if happyLog}<HappyCallModal row={happyLog.row} reasons={happyLog.reasons} reviewUrl={settings.data?.google_review_url ?? ''} onClose={() => happyLog = null} onSaved={saved} />{/if}
{#if fixLog}
  <Modal title={`Confirm the fix · ${fixLog.customer_name || fixLog.reporter_name || `Case #${fixLog.id}`}`} onClose={() => fixLog = null}>
    <p class="day-meta fix-intro">Case #{fixLog.id} · {fixLog.title}{#if fixLog.resolving_technician_name}{' · '}fixed by {fixLog.resolving_technician_name}{/if}</p>
    <CustomerCallForm issue={fixLog} onSaved={() => saved('Customer call saved on the case.')} onCancel={() => fixLog = null} />
  </Modal>
{/if}
{#if composer}<Composer personId={composer.personId ?? composer.activity?.person_id ?? undefined} activity={composer.activity ?? null} mode="log" initialType="call" onSaved={() => saved('Call logged.')} onClose={() => composer = null} />{/if}
{:else}
  <p class="p-5">This queue requires admin, eligible support, or intake-coordinator access to a supported sales pipeline.</p>
{/if}

<style>
  .queue { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; max-width: 1320px; }
  .queue > *, .top > *, .list-head > * { min-width: 0; }
  .top { display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr); gap: 16px; align-items: stretch; }
  .hero { --accent: var(--tym-lime); position: relative; overflow: hidden; display: grid; gap: 12px; align-content: start; min-height: 300px; padding: 26px 28px; }
  .hero::before { content: ''; position: absolute; inset: 0 auto 0 0; width: 6px; background: var(--accent); }
  .hero-head { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
  .hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #c9d3de; }
  .dot { width: 9px; height: 9px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 0 4px color-mix(in srgb, var(--accent) 25%, transparent); }
  .hero-name { font-family: var(--tym-font-display); font-size: 32px; font-weight: 700; line-height: 1.1; letter-spacing: -.01em; color: var(--tym-white); }
  .hero-place { color: #c9d3de; font-size: 14px; }
  .hero-scores { display: flex; flex-wrap: wrap; gap: 8px; }
  .hero-scores span { padding: 4px 11px; border-radius: 999px; background: #1a3252; color: #e3e8ee; font-size: 12.5px; }
  .hero-scores span.low { background: #5a2420; color: #ffd6cf; }
  blockquote { margin: 0; padding: 12px 16px; border-radius: 14px; background: #1a3252; color: var(--tym-white); font-size: 15px; line-height: 1.45; }
  .ask { font-size: 13.5px; color: #c9d3de; }
  .ask strong { margin-right: 6px; color: var(--tym-lime); }
  .hero-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 4px; }
  .hero-actions button { min-height: 46px; padding: 0 18px; border-radius: 14px; font-weight: 600; }
  .call { display: inline-flex; align-items: center; gap: 8px; background: var(--tym-lime); color: var(--tym-navy); font-family: var(--tym-font-display); font-size: 15px; }
  .call:hover { background: var(--tym-lime-600); }
  .light { background: var(--tym-white); color: var(--tym-navy); }
  .ghost { border: 1px solid #2f4a6e; color: var(--tym-white); }
  .ghost:hover { background: #1a3252; }
  .hero-error { color: #ffb4a6; font-size: 13px; }
  .cleared { display: grid; gap: 8px; align-content: center; min-height: 240px; }
  .stats { display: grid; gap: 12px; align-content: start; }
  .summary { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; }
  .big { display: block; font-family: var(--tym-font-display); font-size: 48px; font-weight: 700; line-height: 1; color: var(--tym-navy); }
  .big.small { font-size: 28px; text-align: right; }
  .done p { text-align: right; }
  .late { color: var(--tym-danger); }
  .tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
  .tile { --accent: var(--tym-teal); display: grid; gap: 2px; padding: 12px 12px 10px; border-radius: 16px; background: var(--tym-white); box-shadow: var(--tym-shadow-1); text-align: left; border-top: 3px solid var(--accent); transition: transform var(--tym-dur-fast) var(--tym-ease-out); }
  .tile:hover { transform: translateY(-1px); box-shadow: var(--tym-shadow-2); }
  .tile[aria-pressed='true'] { background: var(--tym-navy); }
  .tile[aria-pressed='true'] .tile-n, .tile[aria-pressed='true'] .tile-l { color: var(--tym-white); }
  .tile-n { font-family: var(--tym-font-display); font-size: 22px; font-weight: 700; color: var(--tym-navy); }
  .tile-l { font-size: 11.5px; color: var(--tym-dark-gray); }
  .saved { padding: 10px 14px; border-radius: 12px; background: var(--tym-lime-100); color: #3d5a00; font-weight: 600; font-size: 13.5px; }
  .list { display: grid; grid-template-columns: minmax(0, 1fr); gap: 4px; padding: 18px 20px; }
  .list > * { min-width: 0; }
  .list-head { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; align-items: center; margin-bottom: 6px; }
  .search { max-width: 280px; }
  .rule { margin: 2px 0 6px; }
  .quiet { border: 0; border-bottom: 1px dashed var(--tym-teal); background: transparent; color: var(--tym-teal); font-weight: 600; }
  .item { --accent: var(--tym-teal); position: relative; gap: 12px; padding: 12px 10px 12px 14px; }
  .item.active { background: #f4f8f8; }
  .stripe { position: absolute; left: 0; top: 10px; bottom: 10px; width: 4px; border-radius: 4px; background: var(--accent); }
  .avatar { flex: none; display: inline-flex; width: 38px; height: 38px; align-items: center; justify-content: center; border-radius: 50%; background: var(--tym-light-carbon); color: var(--tym-navy); font-family: var(--tym-font-display); font-weight: 700; font-size: 13px; }
  .main { flex: 1; min-width: 0; display: grid; gap: 2px; text-align: left; }
  .line1 { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
  .line1 strong { color: var(--tym-navy); font-size: 14.5px; }
  .main:hover strong { text-decoration: underline; }
  .kind { font-size: 11.5px; font-weight: 600; color: var(--accent); }
  .line2, .line3 { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; color: var(--tym-dark-gray); }
  .line3 { font-style: italic; }
  .when { flex: none; }
  .actions { flex: none; display: flex; gap: 6px; }
  .call-mini { background: var(--tym-lime); border-color: var(--tym-lime); font-weight: 600; }
  .call-mini:hover:not(:disabled) { background: var(--tym-lime-600); }
  .rules { font-size: 13px; color: var(--tym-dark-gray); }
  .rules summary { cursor: pointer; font-weight: 600; color: var(--tym-teal); }
  .rules ul { margin-top: 8px; display: grid; gap: 4px; padding-left: 18px; list-style: disc; }
  .fix-intro { margin-bottom: 12px; }
  @media (max-width: 1080px) {
    .top { grid-template-columns: 1fr; }
  }
  @media (max-width: 680px) {
    .hero { padding: 22px 20px; min-height: 0; }
    .list { padding: 14px 12px; }
    .hero-name { font-size: 26px; }
    .hero-actions button { flex: 1; }
    .tiles { grid-template-columns: repeat(4, minmax(0, 1fr)); }
    .tile { padding: 10px 8px 8px; }
    .tile-l { font-size: 10.5px; }
    .summary { align-items: center; }
    .item { flex-wrap: wrap; }
    .main { flex-basis: calc(100% - 60px); }
    .actions { width: 100%; }
    .actions > * { flex: 1; }
    .search { max-width: none; width: 100%; }
  }
</style>
